import React, { useState, useMemo, useRef, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useNavigate, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Eye, Edit3, Trash2 } from 'lucide-react';
import { getSocket, connectAdminSocket } from '../socket';

import { UserRoundPen } from "lucide-react";


type Order = {
  id: number;
  orderNo: string;
  orderRef: string;
  jobType: string;
  orderDate: string;
  estimatedDate: string;
  status: string;
  shippedBy: string;
  shippedDate: string;
  trackingNo: string;
  message?: string;
  description?: string;
  // Notificări
  has_unread_messages?: boolean;
  has_changes?: boolean;
};

const statusClass = (status: string) => {
  switch (status) {
    case 'Created':
      return 'bg-blue-200 text-blue-900 dark:bg-blue-600 dark:text-white';
    case 'Received':
      return 'bg-green-200 text-green-900 dark:bg-green-600 dark:text-white';
    case 'In process':
      return 'bg-yellow-200 text-yellow-900 dark:bg-yellow-600 dark:text-black';
    case 'In pending':
      return 'bg-orange-200 text-orange-900 dark:bg-orange-600 dark:text-white';
    case 'Dispatched':
      return 'bg-purple-200 text-purple-900 dark:bg-purple-600 dark:text-white';
    default:
      return 'bg-gray-200 text-gray-800 dark:bg-gray-500 dark:text-white';
  }
};

const parseOrderDate = (orderDate: string): Date | null => {
  if (!orderDate) return null;
  try {
    // Încearcă să parseze ca ISO string mai întâi
    const isoDate = new Date(orderDate);
    if (!isNaN(isoDate.getTime())) {
      return isoDate;
    }

    // Fallback pentru formatul original
    const parts = orderDate.split(' ')[1]?.split('/');
    const timeParts = orderDate.split(' ')[0]?.split(':');
    if (parts && timeParts) {
      return new Date(
        Number(parts[2]),
        Number(parts[1]) - 1,
        Number(parts[0]),
        Number(timeParts[0]),
        Number(timeParts[1])
      );
    }
    return null;
  } catch {
    return null;
  }
};

// Funcție pentru a verifica dacă un order are notificări
const hasNotifications = (order: Order): boolean => {
  return !!(order.has_unread_messages || order.has_changes);
};

const allColumns = [
  { key: 'orderRef', label: 'Order Ref.' },
  { key: 'jobType', label: 'Job Type' },
  { key: 'orderDate', label: 'Order Date' },
  { key: 'estimatedDate', label: 'Estimated Date' },
  { key: 'status', label: 'Status' },
  { key: 'shippedBy', label: 'Shipped By' },
  { key: 'orderNo', label: 'Order No.' },
  { key: 'trackingNo', label: 'Tracking No.' },
  { key: 'shippedDate', label: 'Shipped Date' },
  { key: 'actions', label: 'Actions' },
];

const MyOrders = () => {
  const [filters, setFilters] = useState({
    orderNo: '',
    orderRef: '',
    orderDate: '',
    status: 'Select All',
  });


  // Profilul userului + dropdown
  const [userProfile, setUserProfile] = useState<{ name: string; email: string; phone?: string; address?: string }>({
    name: '',
    email: '',
  });
  const [showProfileCard, setShowProfileCard] = useState(false);


  const [visibleColumns, setVisibleColumns] = useState<string[]>(allColumns.map(col => col.key));
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const dropdownRef = useRef<HTMLDivElement>(null);

const API_URL = process.env.REACT_APP_API_URL;

const handleDeleteOrder = async (orderId: number, orderRef: string) => {
  const isConfirmed = window.confirm(`Are you sure you want to delete order "${orderRef}"?\n\nThis action cannot be undone.`);
  if (!isConfirmed) return;

  try {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API_URL}/orders/${orderId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!res.ok) {
      const errorData = await res.json();
      throw new Error(errorData.message || "Failed to delete order");
    }

    setOrders(prev => prev.filter(order => order.id !== orderId));
    alert("Order deleted successfully!");
  } catch (error) {
    console.error("Delete error:", error);
    alert(`Error deleting order: ${error instanceof Error ? error.message : "Please try again."}`);
  }
};

const markNotificationsAsRead = async (orderId: number) => {
  try {
    const token = localStorage.getItem("token");
    await fetch(`${API_URL}/orders/${orderId}/mark-read`, {
      method: "PUT",
      headers: { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    setOrders(prev =>
      prev.map(order =>
        order.id === orderId
          ? { ...order, has_unread_messages: false, has_changes: false }
          : order
      )
    );
  } catch (error) {
    console.error("Error marking notifications as read:", error);
  }
};




useEffect(() => {
  connectAdminSocket();

  return () => {
    // opțional: nu deconectăm aici, doar curățăm listeners în alte useEffect-uri
  };
}, []);


  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowColumnDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      const token = localStorage.getItem('token');

      if (!token) {
        alert('Please login first');
        navigate('/auth');
        return;
      }

      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/orders`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const data = await res.json();
          console.log('Orders received from API:', data); // Debug log

          // Mapează datele pentru a fi sigur că sunt în formatul corect
          const mappedOrders: Order[] = data.map((order: any) => ({
            id: order.id,
            orderNo: order.orderno || order.orderNo || '',
            orderRef: order.orderref || order.orderRef || '',
            jobType: order.jobtype || order.jobType || '',
            orderDate: order.orderdate || order.orderDate || '',
            estimatedDate: order.estimateddate || order.estimatedDate || '',
            status: order.status || 'Pending',
            shippedBy: order.shippedby || order.shippedBy || '',
            shippedDate: order.shippeddate || order.shippedDate || '',
            trackingNo: order.trackingno || order.trackingNo || '',
            message: order.message || '',
            description: order.description || '',
            // Notificări
            has_unread_messages: order.has_unread_messages || false,
            has_changes: order.has_changes || false,
          }));

          setOrders(mappedOrders);
          console.log('Mapped orders:', mappedOrders); // Debug log
        } else {
          console.error('Failed to fetch orders:', res.status, res.statusText);
          if (res.status === 401) {
            alert('Session expired. Please login again.');
            navigate('/auth');
          } else {
            alert('Failed to load orders. Please try again.');
          }
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
        alert('Error loading orders. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [navigate]);

  

  // În MyOrders.tsx, înlocuiește useEffect-ul pentru fetchUserProfile cu acesta:

useEffect(() => {
  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      // 🔒 NU MAI CITEȘTE DIN localStorage - doar din backend securizat
      const res = await fetch(`${API_URL}/me`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      
      if (res.ok) {
        const data = await res.json();
        console.log('✅ Profil încărcat securizat în MyOrders:', data);
        
        // Setează doar datele utilizatorului curent autentificat
        setUserProfile({
          name: data.name || '',
          email: data.email || '',
          phone: data.phone || '',
          address: data.address || ''
        });
        
        // Actualizează localStorage cu datele corecte
        localStorage.setItem("userProfile", JSON.stringify(data));
      } else if (res.status === 401) {
        // Token invalid - redirecționează la login
        localStorage.removeItem("token");
        localStorage.removeItem("userProfile");
        navigate('/auth');
      } else {
        console.error('Eroare la încărcarea profilului:', res.status);
      }
    } catch (err) {
      console.error('Eroare rețea încărcare profil:', err);
    }
  };

  fetchUserProfile();
}, [navigate]); // Adaugă navigate în dependency array




  // Socket.IO pentru notificări real-time
  useEffect(() => {
    console.log('Setting up socket listeners in MyOrders');

    // ✅ ADAUGĂ ACEST COD LA ÎNCEPUT:
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Decode token ca să obții userId
        const payload = JSON.parse(atob(token.split('.')[1]));
        getSocket()?.emit('joinUser', payload.userId);
        console.log('User joined room:', payload.userId);
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }

    const handleUserNotification = (data: any) => {
      console.log('=== NOTIFICARE PRIMITĂ ===');
      console.log('Data primită:', data);

      // ✅ VERIFICĂ CĂ NOTIFICAREA E PENTRU ACEST USER
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));

          // Dacă userId din notificare nu match cu userId din token, ignoră
          if (data.userId !== payload.userId) {
            console.log('Notificare ignorată - nu e pentru acest user');
            return;
          }
        } catch (error) {
          console.error('Error verifying user:', error);
          return;
        }
      }

      // Actualizează order-ul specific cu notificare
      setOrders(prev => {
        const updatedOrders = prev.map(order => {
          if (order.id === data.orderId || order.id === Number(data.orderId)) {
            console.log('MATCH GĂSIT! Actualizez order:', order.id);
            return { ...order, has_unread_messages: true };
          }
          return order;
        });
        return updatedOrders;
      });
    };

    getSocket()?.on('userNotification', handleUserNotification);

    return () => {
      getSocket()?.off('userNotification', handleUserNotification);
    };
  }, []); // IMPORTANT: Șterge orders din dependency array



  // Refresh periodic pentru notificări (backup pentru socket)
  useEffect(() => {
    const refreshNotifications = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        const res = await fetch(`${API_URL}/orders/notifications`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.ok) {
          const notifications = await res.json();

          // Actualizează doar notificările, nu toate datele
          setOrders(prev => prev.map(order => {
            const notification = notifications.find((n: any) => n.orderId === order.id);
            return notification ? {
              ...order,
              has_unread_messages: notification.has_unread_messages,
              has_changes: notification.has_changes
            } : order;
          }));
        }
      } catch (error) {
        console.error('Error refreshing notifications:', error);
      }
    };

    // Refresh la fiecare 30 secunde
    const interval = setInterval(refreshNotifications, 30000);

    return () => clearInterval(interval);
  }, []);


  useEffect(() => {
  const token = localStorage.getItem('token');
  if (!token) return;

  const handleProfileUpdate = (data: any) => {
    console.log("Profile updated by admin in MyOrders:", data);
    
    // Actualizează profilul afișat în dropdown
    setUserProfile({
      name: data.client.name || '',
      email: data.client.email || '',
      phone: data.client.phone || '',
      address: data.client.address || ''
    });

    // Actualizează și localStorage
    localStorage.setItem("userProfile", JSON.stringify(data.client));
  };

  getSocket()?.on('profileUpdated', handleProfileUpdate);

  return () => {
    getSocket()?.off('profileUpdated', handleProfileUpdate);
  };
}, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleReset = () => {
    setFilters({ orderNo: '', orderRef: '', orderDate: '', status: 'Select All' });
    setStartDate(null);
    setEndDate(null);
  };

  const filteredOrders = useMemo(() => {
    return orders
      .filter((order) => {
        const orderNoMatch = filters.orderNo === '' || order.orderNo.toLowerCase().includes(filters.orderNo.toLowerCase());
        const orderRefMatch = filters.orderRef === '' || order.orderRef.toLowerCase().includes(filters.orderRef.toLowerCase());
        const statusMatch = filters.status === 'Select All' || order.status === filters.status || (!order.status && filters.status === 'Select All');

        let dateMatch = true;
        if (startDate && endDate) {
          const orderDateObj = parseOrderDate(order.orderDate);
          if (orderDateObj) {
            dateMatch = orderDateObj >= startDate && orderDateObj <= new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59);
          }
        }

        return orderNoMatch && orderRefMatch && statusMatch && dateMatch;
      })
      .sort((a, b) => {
        // Sortează după orderDate descrescător (cea mai recentă sus)
        const dateA = parseOrderDate(a.orderDate);
        const dateB = parseOrderDate(b.orderDate);

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        return dateB.getTime() - dateA.getTime(); // Descrescător
      });
  }, [orders, filters, startDate, endDate]);
  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-lg">Loading orders...</div>
        </div>
      </Layout>
    );
  }




  return (
    <Layout>
      <main className="flex-grow max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="text-2xl sm:text-4xl font-extrabold uppercase mb-10 text-center tracking-wide">
          My Orders
        </h1>

        {/* Account Info Dropdown */}
<div className="flex justify-end mb-6 relative">
  <button
    onClick={() => setShowProfileCard(!showProfileCard)}
    onMouseEnter={() => setShowProfileCard(true)}
    onMouseLeave={() => setShowProfileCard(false)}
    className="flex items-center justify-center w-10 h-10 bg-gray-700 text-white rounded-full shadow hover:bg-green-600 transition"
    title="My Account"
  >
    <UserRoundPen className="w-6 h-6" />
  </button>

  {showProfileCard && (
    <div
      onMouseEnter={() => setShowProfileCard(true)}
      onMouseLeave={() => setShowProfileCard(false)}
      className="absolute right-0 mt-2 w-80 bg-white dark:bg-neutral-600 rounded-xl shadow-2xl p-6 border border-gray-200 dark:border-neutral-700 z-50"
    >
      <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
        <UserRoundPen className="w-5 h-5 text-green-600" />
        Account Info
      </h3>
      <p className="truncate"><span className="font-medium">Name:</span> {userProfile.name}</p>
      <p className="truncate"><span className="font-medium">Email:</span> {userProfile.email}</p>
      {userProfile.phone && <p><span className="font-medium">Phone:</span> {userProfile.phone}</p>}
      {userProfile.address && <p><span className="font-medium">Address:</span> {userProfile.address}</p>}

      <button
        onClick={() => navigate('/account')}
        className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-md transition"
      >
        Settings
      </button>
    </div>
  )}
</div>



        {/* Filtre */}
        <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-6 gap-x-4 mb-10 bg-gray-50 dark:bg-neutral-600 p-4 sm:p-6 rounded-xl shadow-lg">
          <div className="flex flex-col">
            <label htmlFor="orderNo" className="mb-2 font-semibold">
              Order No.
            </label>
            <input
              id="orderNo"
              name="orderNo"
              type="text"
              value={filters.orderNo}
              onChange={handleChange}
              placeholder="Search by Order No."
              className="border border-gray-300 dark:border-neutral-700 rounded-md px-4 py-2 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition"
            />
          </div>

          <div className="flex flex-col">
            <label htmlFor="orderRef" className="mb-2 font-semibold">
              Order Ref.
            </label>
            <input
              id="orderRef"
              name="orderRef"
              type="text"
              value={filters.orderRef}
              onChange={handleChange}
              placeholder="Search by Order Ref."
              className="border border-gray-300 dark:border-neutral-700 rounded-md px-4 py-2 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition"
            />
          </div>

          <div className="flex flex-col">
            <label className="mb-2 font-semibold">Order Date</label>
            <div className="flex gap-2 items-center">
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                dateFormat="dd/MM/yyyy"
                placeholderText="Start date"
                className="w-full border border-gray-300 dark:border-neutral-700 rounded-md px-4 py-2 bg-white dark:bg-neutral-700 text-black dark:text-white"
              />
              <span className="text-sm text-gray-600 dark:text-gray-300">to</span>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate ?? undefined}
                dateFormat="dd/MM/yyyy"
                placeholderText="End date"
                className="w-full border border-gray-300 dark:border-neutral-700 rounded-md px-4 py-2 bg-white dark:bg-neutral-700 text-black dark:text-white"
              />
            </div>
          </div>

          <div className="flex flex-col">
            <label htmlFor="status" className="mb-2 font-semibold">
              Status
            </label>
            <select
              id="status"
              name="status"
              value={filters.status}
              onChange={handleChange}
              className="border border-gray-300 dark:border-neutral-700 rounded-md px-4 py-2 bg-white dark:bg-neutral-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition"
            >
              <option>Select All</option>
              <option>Created</option>
              <option>Received</option>
              <option>In process</option>
              <option>In pending</option>
              <option>Dispatched</option>
            </select>
          </div>
        </section>

        <section className="flex flex-col items-start gap-4 mb-10 relative">
          {/* Primele 3 butoane pe o singură linie */}
          <div className="flex gap-4 flex-wrap w-full max-w-full">
            <button
              onClick={() => console.log('Search with filters:', filters)}
              type="button"
              className="flex-shrink-0 px-4 py-2 text-sm sm:text-base bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md shadow-md transition"
            >
              Search
            </button>

            <button
              onClick={() => navigate('/create-order')}
              type="button"
              className="flex-shrink-0 px-4 py-2 text-sm sm:text-base bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md shadow-md transition"
            >
              Create Order
            </button>

            <button
              onClick={handleReset}
              type="button"
              className="flex-shrink-0 px-4 py-2 text-sm sm:text-base bg-red-600 hover:bg-red-700 text-white font-semibold rounded-md shadow-md transition"
            >
              Reset
            </button>
          </div>

          {/* Butonul Column Visibility */}
          <div className="w-full">
            <button
              onClick={() => setShowColumnDropdown(!showColumnDropdown)}
              type="button"
              className="w-full px-4 py-2 text-sm sm:text-base bg-gray-800 hover:bg-gray-700 text-white font-semibold rounded-md shadow-md transition"
            >
              Column Visibility
            </button>

            {showColumnDropdown && (
              <div
                ref={dropdownRef}
                className="absolute z-20 mt-2 w-64 bg-white dark:bg-neutral-600 border border-gray-300 dark:border-neutral-600 rounded-md shadow-lg p-4"
              >
                {allColumns.map((col) => (
                  <label key={col.key} className="flex items-center mb-2 text-sm">
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(col.key)}
                      onChange={() =>
                        setVisibleColumns((prev) =>
                          prev.includes(col.key)
                            ? prev.filter((key) => key !== col.key)
                            : [...prev, col.key]
                        )
                      }
                      className="mr-2"
                    />
                    {col.label}
                  </label>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Tabel */}
        <section className="w-full overflow-x-auto rounded-lg shadow-lg border border-gray-200 dark:border-neutral-700">
          <div className="w-full">
            <table className="w-full bg-white dark:bg-neutral-700 min-w-[800px] text-xs sm:text-sm">
              <thead className="sticky top-0 bg-gray-100 dark:bg-neutral-600 text-gray-700 dark:text-gray-300">
                <tr>
                  {allColumns
                    .filter(col => visibleColumns.includes(col.key))
                    .map(col => (
                      <th
                        key={col.key}
                        className={`p-2 sm:p-3 font-semibold text-left ${col.key === 'actions' ? 'w-24 sm:w-32' :
                          col.key === 'orderRef' ? 'w-32 sm:w-40' :
                            col.key === 'status' ? 'w-24 sm:w-32' :
                              'w-28 sm:w-36'
                          }`}
                      >
                        {col.label}
                      </th>
                    ))}
                </tr>
              </thead>

              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td
                      colSpan={visibleColumns.length}
                      className="p-6 text-center text-gray-500 dark:text-gray-400"
                    >
                      {orders.length === 0 ? 'No orders found. Create your first order!' : 'No orders match your filters. Try adjusting your search criteria.'}
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map((order) => (
                    <tr key={order.id} className="border-b border-gray-200 dark:border-neutral-600 hover:bg-gray-50 dark:hover:bg-neutral-600 transition">
                      {visibleColumns.includes('orderRef') && (
                        <td className="p-3 truncate max-w-[160px]" title={order.orderRef}>
                          <div className="flex items-center gap-2">
                            <span>{order.orderRef || '-'}</span>
                            {hasNotifications(order) && (
                              <div
                                className="w-2 h-2 bg-green-500 rounded-full animate-pulse"
                                title="You have new notifications"
                              />
                            )}
                          </div>
                        </td>
                      )}
                      {visibleColumns.includes('jobType') && (
                        <td className="p-3 truncate max-w-[160px]" title={order.jobType}>
                          {order.jobType || '-'}
                        </td>
                      )}
                      {visibleColumns.includes('orderDate') && (
                        <td className="p-3 truncate max-w-[160px]">
                          {order.orderDate ? new Date(order.orderDate).toLocaleDateString() : '-'}
                        </td>
                      )}
                      {visibleColumns.includes('estimatedDate') && (
                        <td className="p-3 truncate max-w-[160px]">
                          {order.estimatedDate ? new Date(order.estimatedDate).toLocaleDateString() : '-'}
                        </td>
                      )}
                      {visibleColumns.includes('status') && (
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusClass(order.status)}`}>
                            {order.status || 'Pending'}
                          </span>
                        </td>
                      )}
                      {visibleColumns.includes('shippedBy') && (
                        <td className="p-3 truncate max-w-[160px]" title={order.shippedBy}>
                          {order.shippedBy || '-'}
                        </td>
                      )}
                      {visibleColumns.includes('orderNo') && (
                        <td className="p-3 truncate max-w-[160px]" title={order.orderNo}>
                          {order.orderNo || '-'}
                        </td>
                      )}
                      {visibleColumns.includes('trackingNo') && (
                        <td className="p-3 truncate max-w-[160px]" title={order.trackingNo}>
                          {order.trackingNo || '-'}
                        </td>
                      )}
                      {visibleColumns.includes('shippedDate') && (
                        <td className="p-3 truncate max-w-[160px]">
                          {order.shippedDate ? new Date(order.shippedDate).toLocaleDateString() : '-'}
                        </td>
                      )}
                      {visibleColumns.includes('actions') && (
                        <td className="p-3">
                          <div className="flex items-center gap-1 sm:gap-3">                            {/* View - întotdeauna vizibil */}
                            <Link
                              to={`/orders/${order.id}`}
                              onClick={() => {
                                if (hasNotifications(order)) {
                                  markNotificationsAsRead(order.id);
                                }
                              }}
                              className="flex items-center justify-center w-8 h-8 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200 relative"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                              {hasNotifications(order) && (
                                <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                              )}
                            </Link>

                            {/* Edit și Delete - doar dacă status este Created */}
                            {order.status === 'Created' && (
                              <>
                                <Link
                                  to={`/edit-order/${order.id}`}
                                  className="flex items-center justify-center w-8 h-8 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-all duration-200"
                                  title="Edit Order"
                                >
                                  <Edit3 className="w-4 h-4" />
                                </Link>

                                <button
                                  onClick={() => handleDeleteOrder(order.id, order.orderRef)}
                                  className="flex items-center justify-center w-8 h-8 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                                  title="Delete Order"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </main>
    </Layout>



  );
};

export default MyOrders;